import React, { useState, useEffect, useRef } from 'react';
import { Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react';

const TestimonialsSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const sectionRef = useRef(null);

  const testimonials = [
    {
      author: "Lucius Qayin",
      title: "Dark Fantasy Author",
      image: "/assets/images/authors/lucius.jpg",
      quote: "WriteToSite transformed my online presence completely. My book sales increased by 40% within the first month of launching my new website. Their understanding of author needs is unmatched.",
      stars: 5,
      bookCover: "/assets/images/books/lucius-book.png"
    },
    {
      author: "Carter Case",
      title: "Children's Series Writer",
      image: "/assets/images/authors/carter.png",
      quote: "The team at WriteToSite created exactly what I needed — a professional platform that makes it easy to connect with my readers. The chapter preview feature is brilliant! They built the perfect marketing machine for my Ned and Nancy series.",
      stars: 5,
      bookCover: "/assets/images/books/carter-book.png"
    },
    {
      author: "Abigail Landon",
      title: "Self-Help Author",
      image: "/assets/images/authors/abigail.jpg",
      quote: "I couldn't be happier with my author website. It's beautiful, functional, and has helped me build a real community around my books. The reader engagement features are outstanding.",
      stars: 5,
      bookCover: "/assets/images/books/abigail-book.png"
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const navigate = (direction) => {
    if (isAnimating) return;
    setIsAnimating(true);

    if (direction === 'next') {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    } else {
      setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    }

    setTimeout(() => setIsAnimating(false), 500);
  };

  return (
    <section ref={sectionRef} className="relative py-24 lg:py-32 bg-cream-50 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-0 w-[400px] h-[400px] bg-gold-100/50 rounded-full blur-3xl -translate-y-1/2 -translate-x-1/2" />
        <div className="absolute bottom-0 right-0 w-[300px] h-[300px] bg-ink-900/5 rounded-full blur-3xl translate-x-1/2" />
      </div>

      {/* Decorative Quote */}
      <div className="absolute top-20 right-20 opacity-[0.03]">
        <Quote className="w-64 h-64 text-ink-900" />
      </div>

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        {/* Header */}
        <div className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center gap-3 mb-6">
            <span className="w-8 h-px bg-gold-500" />
            <span className="text-gold-600 text-sm font-sans tracking-[0.2em] uppercase">
              Testimonials
            </span>
            <span className="w-8 h-px bg-gold-500" />
          </div>

          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-ink-900 mb-6">
            Trusted by Authors
            <span className="block mt-2 text-gradient-gold">Worldwide</span>
          </h2>

          <p className="font-body text-lg text-ink-600 leading-relaxed">
            Join the growing community of successful authors who've transformed their digital presence.
          </p>
        </div>

        {/* Testimonial Card */}
        <div className={`max-w-5xl mx-auto transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="relative bg-cream-100 border border-cream-200 p-8 lg:p-12">
            {/* Gold Corner Accents */}
            <div className="absolute top-0 left-0 w-16 h-16 border-t-2 border-l-2 border-gold-400" />
            <div className="absolute bottom-0 right-0 w-16 h-16 border-b-2 border-r-2 border-gold-400" />

            <div className="grid md:grid-cols-5 gap-8 lg:gap-12 items-center">
              {/* Book Cover */}
              <div className="md:col-span-2 flex justify-center">
                <div className="relative">
                  {/* Book Shadow */}
                  <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-4/5 h-4 bg-ink-900/10 blur-xl rounded-full" />

                  {/* Book */}
                  <div
                    className={`relative transform transition-all duration-500 ${
                      isAnimating ? 'scale-95 opacity-0' : 'scale-100 opacity-100'
                    }`}
                  >
                    <img
                      src={testimonials[currentIndex].bookCover}
                      alt={`${testimonials[currentIndex].author}'s book`}
                      className="w-48 lg:w-56 h-auto shadow-book rounded-sm"
                    />

                    {/* Decorative Border */}
                    <div className="absolute -inset-3 border border-gold-300/30 rounded-sm pointer-events-none" />
                  </div>
                </div>
              </div>

              {/* Testimonial Content */}
              <div
                className={`md:col-span-3 transition-all duration-500 ${
                  isAnimating ? 'opacity-0 translate-x-4' : 'opacity-100 translate-x-0'
                }`}
              >
                {/* Stars */}
                <div className="flex gap-1 mb-6">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className="w-5 h-5 text-gold-500 fill-gold-500"
                    />
                  ))}
                </div>

                {/* Quote */}
                <blockquote className="relative mb-8">
                  <Quote className="absolute -top-2 -left-2 w-8 h-8 text-gold-300 opacity-50" />
                  <p className="font-accent text-xl lg:text-2xl text-ink-700 italic leading-relaxed pl-6">
                    {testimonials[currentIndex].quote}
                  </p>
                </blockquote>

                {/* Author Info */}
                <div className="flex items-center gap-4">
                  <img
                    src={testimonials[currentIndex].image}
                    alt={testimonials[currentIndex].author}
                    className="w-14 h-14 rounded-full object-cover border-2 border-gold-200"
                  />
                  <div>
                    <div className="font-display text-lg font-semibold text-ink-900">
                      {testimonials[currentIndex].author}
                    </div>
                    <div className="font-sans text-sm text-ink-500">
                      {testimonials[currentIndex].title}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-center gap-6 mt-8">
            {/* Prev Button */}
            <button
              onClick={() => navigate('prev')}
              className="w-12 h-12 flex items-center justify-center border border-ink-200 text-ink-600 hover:border-gold-400 hover:text-gold-600 transition-all duration-300"
              aria-label="Previous testimonial"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            {/* Dots */}
            <div className="flex gap-3">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    if (!isAnimating && index !== currentIndex) {
                      setIsAnimating(true);
                      setCurrentIndex(index);
                      setTimeout(() => setIsAnimating(false), 500);
                    }
                  }}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    index === currentIndex
                      ? 'w-8 bg-gold-500'
                      : 'w-2 bg-ink-200 hover:bg-ink-300'
                  }`}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>

            {/* Next Button */}
            <button
              onClick={() => navigate('next')}
              className="w-12 h-12 flex items-center justify-center border border-ink-200 text-ink-600 hover:border-gold-400 hover:text-gold-600 transition-all duration-300"
              aria-label="Next testimonial"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
